/**
 * Core Carousel Type Definitions
 *
 * Central hub for all TypeScript interfaces and enums used throughout the app.
 * These types define the data models that flow between components.
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Slide role classification for content structure.
 * Used by AI generation to create proper carousel flow.
 */
export enum SlideType {
  COVER = 'COVER',       // First slide: Hook/attention grabber
  CONTENT = 'CONTENT',   // Middle slides: Educational/informational
  CTA = 'CTA'            // Last slide: Call to action
}

/**
 * Visual template/style for the carousel.
 * Each style has its own component (TwitterSlide, StorytellerSlide).
 */
export enum CarouselStyle {
  TWITTER = 'TWITTER',           // Tweet screenshot aesthetic (text-focused)
  APPLE_NOTES = 'APPLE_NOTES',   // Reserved for future implementation
  STORYTELLER = 'STORYTELLER',   // Cinematic image overlays (image-focused)
}

// ============================================================================
// TYPE ALIASES
// ============================================================================

/**
 * Post dimensions using CSS aspect-ratio format.
 * Supported ratios for slide export.
 */
export type AspectRatio = '1/1' | '4/5' | '9/16' | '16/9';

/**
 * Color theme for slides.
 */
export type Theme = 'LIGHT' | 'DARK';

/**
 * Font style options for slides.
 * - MODERN: Clean sans-serif (system default)
 * - SERIF: Classic serif font for elegant/editorial look
 * - TECH: Monospace/technical font for developer/startup content
 * - ELEGANT: Lora serif for refined/editorial look
 * - BOLD: Montserrat geometric sans-serif for strong presence
 * - MINIMAL: Raleway light sans-serif for clean design
 * - HANDWRITTEN: Caveat cursive for personal/authentic feel
 */
export type FontStyle = 'MODERN' | 'SERIF' | 'TECH' | 'ELEGANT' | 'BOLD' | 'MINIMAL' | 'HANDWRITTEN';

/**
 * Position options for slide number indicator.
 * - center: Centered at bottom of slide (default)
 * - bottom-right: Bottom-right corner of slide
 */
export type SlideNumberPosition = 'center' | 'bottom-right';

/**
 * Content layout options for element ordering (Twitter style).
 * Controls where the illustration image appears relative to text content.
 * - default: Header → All Content → Image (current behavior)
 * - image-after-title: Header → Title(H1/H2) → Image → Body text
 * - image-first: Header → Image → All Content
 */
export type ContentLayout = 'default' | 'image-after-title' | 'image-first';

/**
 * Text alignment options for slide content.
 * Controls horizontal alignment of text elements.
 */
export type TextAlignment = 'left' | 'center' | 'right';

/**
 * Language options for AI-generated content.
 * Controls the language used in carousel text generation and refinement.
 */
export type Language = 'pt-BR' | 'en' | 'es';

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Global layout settings for slides.
 * Controls padding, text spacing, and image positioning.
 * Per-slide overrides can be set in Slide interface.
 */
export interface LayoutSettings {
  contentPadding: number;      // 32-96 (px), default 64
  imageZoom: number;           // 0.5-2.0 (50%-200%), default 1.0 (Twitter only) - zoom in/out on image
  imageMargin: number;         // 0-32 (px), default 0 (Twitter only)
  textLineHeight: number;      // 1.2-2.0, default 1.5
  paragraphGap: number;        // 0.5-2.0 (rem), default 1
  textAlignment: TextAlignment; // left, center, right - default 'left'
}

/**
 * Target audience / avatar data for the "Conteúdo Notável" methodology.
 * Collected during onboarding and stored in cai_profiles.preferences.
 * Used to personalize AI-generated carousel content.
 *
 * Flow:
 * 1. User writes free-form text (bioDescription)
 * 2. AI generates a magnetic bio + structured avatar data
 * 3. Structured data enriches carousel generation prompts
 */
export interface AvatarData {
  /** Free-form text written by the user describing who they are */
  bioDescription?: string;
  /** Polished magnetic bio generated by AI */
  magneticBio?: string;

  // --- AI-generated structured fields ---
  /** Specific target audience (e.g., "Mulheres 40+ na menopausa") */
  publicoAlvo?: string;
  /** Identified pain points */
  doresIdentificadas?: string[];
  /** Identified desires / goals */
  desejosIdentificados?: string[];
  /** Unique positioning / defended beliefs */
  posicionamento?: string;
  /** Method or approach used */
  metodoAbordagem?: string;
  /** Suggested content themes for carousels */
  temasConteudo?: string[];

  // Legacy fields (kept for backward compatibility with existing data)
  nicho: string;            // Content niche (e.g., "Saúde e Fitness", "Desenvolvimento Pessoal")
  demografico: string;      // Target demographics (e.g., "Mulheres entre 35-50 anos")
  dores: string;            // Pain points (e.g., "Ansiedade, peso extra, baixa autoestima")
  desejos: string;          // Desires (e.g., "Emagrecer de forma saudável, ter mais energia")
  crencas: string;          // Limiting beliefs (e.g., "Preciso passar fome para emagrecer")
  objecoes: string;         // Objections (e.g., "Não tenho tempo", "Já tentei de tudo")
  medos: string;            // Fears (e.g., "Nunca conseguir mudar")
  momentoDeVida: string;    // Life phase (e.g., "Menopausa, filhos crescidos")
}

/**
 * Result returned by the AI avatar generation endpoint.
 */
export interface GeneratedAvatarResult {
  magneticBio: string;
  publicoAlvo: string;
  dores: string[];
  desejos: string[];
  posicionamento: string;
  metodo: string;
  temasConteudo: string[];
}

/**
 * User/creator profile displayed on slides.
 */
export interface Profile {
  name: string;       // Display name (e.g., "John Doe")
  handle: string;     // Username without @ (e.g., "johndoe")
  avatarUrl: string;  // Profile image URL or base64 data URI
}

/**
 * Individual slide data model.
 *
 * IMAGE PROPERTIES EXPLAINED:
 * These properties work together to control image display:
 *
 * - showImage: Master toggle - if false, no image is shown
 * - imageUrl: The actual image (base64 data URI or URL)
 * - imageScale: How much vertical space the image takes (10-90%)
 *   - Twitter: Image appears below text
 *   - Storyteller: Image height from top of slide
 *
 * - overlayImage (Storyteller only):
 *   - true/undefined: Overlay mode - text floats over image with gradient fade
 *   - false: Split mode - hard line between image and text
 *
 * - imageOffsetY: Vertical crop/alignment (0-100, default 50 = centered)
 *   Controls which part of the image is visible (like CSS object-position)
 *
 * - gradientHeight (Storyteller overlay only): Fade overlay intensity (0-100%)
 *   Higher = more gradual fade from image to background
 */
export interface Slide {
  id: string;                    // Unique identifier (UUID)
  type: SlideType;               // Role in carousel flow
  content: string;               // Markdown-formatted text content
  imageUrl?: string;             // Image source (data URI or URL)
  showImage: boolean;            // Whether to display an image
  imagePrompt?: string;          // AI prompt for (re)generating image
  imageScale?: number;           // Image height percentage (10-90)
  overlayImage?: boolean;        // Storyteller: overlay vs split mode
  imageOffsetY?: number;         // Vertical image alignment (0-100)
  gradientHeight?: number;       // Storyteller: gradient overlay size (0-100)
  fontStyle?: FontStyle;         // Per-slide font override (undefined = use global)
  fontScale?: number;            // Per-slide font size multiplier (decimal 0.5-2.0 = 50%-200%, undefined = use global)

  // Content layout (Twitter style)
  contentLayout?: ContentLayout; // Element ordering (default = 'default')

  // Background image (full-bleed with color overlay)
  showBackgroundImage?: boolean;      // Master toggle for background image
  backgroundImageUrl?: string;        // Full-bleed background image source
  backgroundOverlayColor?: string;    // Overlay color (hex, default: theme-based)
  backgroundOverlayOpacity?: number;  // Overlay opacity 0-100 (default: 50)

  // Per-slide layout overrides (undefined = use global LayoutSettings)
  contentPadding?: number;            // 32-96 (px)
  imageZoom?: number;                 // 0.5-2.0 (50%-200%), Twitter only
  imageMargin?: number;               // 0-32 (px), Twitter only
  textLineHeight?: number;            // 1.2-2.0
  paragraphGap?: number;              // 0.5-2.0 (rem)
  backgroundTextColor?: string;       // hex color, for background image mode

  // Per-slide theme override (undefined = use global theme)
  theme?: Theme;                      // LIGHT or DARK

  // Spacing for image-after-title layout (Twitter style)
  imageTextSpacing?: number;          // 0-64 (px), spacing between image and body text

  // Text alignment (undefined = use global)
  textAlignment?: TextAlignment;      // left, center, right

  // AI generation metadata (Conteúdo Notável methodology)
  gatilhosUsados?: string[];      // Attention triggers used (e.g., ["Recompensa", "Mistério"]) - primarily for COVER
  elementosUsados?: string[];     // Content elements used (e.g., ["Pontos de Identificação", "Lista Numerada"]) - for CONTENT
  tipoCta?: string;               // CTA type (e.g., "Compartilhamento + Comentário") - for CTA slides
}

/**
 * Pre-creation analysis from the "Conteúdo Notável" methodology.
 * Generated by AI before creating slides to ensure strategic alignment.
 */
export interface AnalisePreCriacao {
  desejoPrincipal: string;          // Main desire of the target audience
  dorEspecifica: string;            // Specific pain point addressed
  crencaDesafiada: string;          // Popular belief being challenged
  informacaoNova: string;           // New/contradictory information brought
  moedaSocialAtivada: string;       // Social currency category activated
  pontosIdentificacao: string[];    // Ultra-specific identification points
}

/**
 * Post-generation validation checklist from "Conteúdo Notável" methodology.
 * Each field indicates whether the carousel meets that quality criterion.
 */
export interface ChecklistValidacao {
  informacaoNova: boolean;                    // Has new/contradictory information?
  baixoCarregamentoCognitivo: boolean;        // Easy to understand (simple language)?
  moedaSocial: boolean;                       // Activates social currency?
  brevidadeInteligente: boolean;              // Smart brevity (max value, min words)?
  pontosIdentificacaoEspecificos: boolean;    // Ultra-specific identification points?
  valorPratico: boolean;                      // Delivers practical value?
  fluxoNatural: boolean;                      // Natural flow between slides?
  coverIrresistivel: boolean;                 // Irresistible cover slide?
  ctaConectado: boolean;                      // CTA connected to built desire?
}

/**
 * Complete carousel project data.
 * Used for saving/loading projects via JSON export/import.
 *
 * Includes all slides, profile info, and global settings so the
 * entire project can be restored exactly as it was.
 */
export interface CarouselProject {
  id: string;                      // Unique project identifier
  name: string;                    // User-friendly project name
  style: CarouselStyle;            // Visual template (Twitter/Storyteller)
  aspectRatio: AspectRatio;        // Slide dimensions
  profile: Profile;                // Creator info (name, handle, avatar)
  slides: Slide[];                 // All slide content and images

  // Global settings
  theme: Theme;                    // Light/Dark mode
  accentColor: string;             // Highlight color (hex)
  showAccent: boolean;             // Whether accent color is enabled
  showSlideNumbers: boolean;       // Display slide count (e.g., "1 • 7")
  showVerifiedBadge: boolean;      // Display verified checkmark
  headerScale: number;             // Header/footer size multiplier (0.5-2.0)
  fontStyle: FontStyle;            // Global font family
  fontScale: number;               // Global font size multiplier (decimal 0.5-2.0 = 50%-200%)
  globalImageStyle: string;        // Image generation style prefix
  layoutSettings?: LayoutSettings; // Global layout settings (padding, spacing, etc.)
  language?: Language;             // Language for AI-generated content (default: 'pt-BR')
  nameSource?: 'cover_auto' | 'manual' | 'legacy_topic'; // How project name should be maintained

  // AI generation metadata (Conteúdo Notável methodology)
  analisePreCriacao?: AnalisePreCriacao;       // Pre-creation strategic analysis
  checklistValidacao?: ChecklistValidacao;     // Post-generation quality checklist
  carouselFormat?: 'narrative' | 'list';       // Classification used for image distribution rules

  // Demo flag (onboarding tour projects)
  isDemo?: boolean;                            // True for demo projects created by the onboarding tour

  // Thumbnail
  thumbnailUrl?: string;             // Base64 JPEG thumbnail of the first slide

  // Metadata
  createdAt: string;               // ISO timestamp of creation
  updatedAt: string;               // ISO timestamp of last modification
}

/**
 * Uploaded document for AI carousel generation.
 * Supports PDF (vision), TXT, Markdown, and Image files.
 */
export interface UploadedDocument {
  name: string;                    // Original filename
  type: 'pdf' | 'txt' | 'md' | 'image';  // File type
  content: string;                 // Extracted text (for txt/md)
  base64?: string;                 // Base64 data (for pdf/image - vision API)
  mimeType?: string;               // MIME type (for pdf/image)
  size: number;                    // File size in bytes
}

/**
 * Onboarding wizard step identifiers.
 * Controls which screen is displayed in CreatePage.
 *
 * Profile is now managed separately via the Instagram Profile Modal
 * (accessible from Dashboard), not part of the carousel creation flow.
 */
export type AppStep =
  | 'FORMAT_SELECT'        // Step 1: Choose style
  | 'METHOD_SELECT'        // Step 2: AI or Manual
  | 'AI_INPUT'             // Step 3: AI topic input
  | 'WORKSPACE';           // Step 4: Main editor

// ============================================================================
// ONBOARDING TOUR TYPES
// ============================================================================

/**
 * Tooltip position relative to the target element.
 * 'center' = floating modal with no spotlight.
 */
export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right' | 'center';

/**
 * A single step within an onboarding chapter.
 * Each step highlights one element and shows a tooltip with explanation.
 */
export interface OnboardingStep {
  /** Unique step identifier */
  id: string;
  /** CSS selector for the target element (e.g. '[data-tour="dashboard-stats"]') */
  targetSelector: string;
  /** Alternative selector for mobile layout */
  mobileTargetSelector?: string;
  /** i18n key for the tooltip title */
  titleKey: string;
  /** i18n key for the tooltip description */
  descriptionKey: string;
  /** Tooltip position relative to target */
  placement: TooltipPlacement;
  /** Alternative placement for mobile */
  mobilePlacement?: TooltipPlacement;
  /** Whether to show the spotlight cutout (false = centered modal) */
  spotlight: boolean;
  /** Only show on desktop (>= 768px) */
  desktopOnly?: boolean;
  /** Only show on mobile (< 768px) */
  mobileOnly?: boolean;
  /** Override the chapter route for this specific step */
  route?: string;
  /** If true, user must click the highlighted element to advance (no Next button) */
  interactive?: boolean;
  /** Action to dispatch before showing this step (e.g. 'open-settings-flyout') */
  beforeShowAction?: string;
}

/**
 * A group of related onboarding steps.
 * Steps within a chapter share a common route.
 */
export interface OnboardingChapter {
  /** Unique chapter identifier */
  id: string;
  /** i18n key for the chapter title */
  titleKey: string;
  /** Base route for this chapter (e.g. '/', '/create', '/editor/:id') */
  route: string;
  /** Ordered list of steps */
  steps: OnboardingStep[];
}

/**
 * Persistent state for the onboarding tour.
 * Stored in localStorage and synced to Supabase preferences.
 */
export interface OnboardingState {
  isActive: boolean;
  currentChapterId: string;
  currentStepIndex: number;
  completedChapterIds: string[];
  dismissed: boolean;
  completedAt: string | null;
  version: number;
  /** Full route path for resuming (e.g. '/editor/abc-123') */
  currentRoute?: string;
  /** Project ID used for editor tour chapters (user's real project) */
  projectId?: string;
  /**
   * Tour phase:
   * - 'phase1': Chapters 1-3 (dashboard/creation)
   * - 'waiting': Phase 1 done, waiting for user to create a project and enter editor
   * - 'phase2': Chapters 4-8 (editor tour)
   */
  phase?: 'phase1' | 'waiting' | 'phase2';
}

export const ONBOARDING_TOUR_VERSION = 2;
export const ONBOARDING_STORAGE_KEY = 'cai_onboarding_state';

// ============================================================================
// PLANS & SUBSCRIPTIONS
// ============================================================================

export type BillingPeriod = 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
export type SubscriptionStatus = 'ACTIVE' | 'PAST_DUE' | 'CANCELED' | 'PENDING' | 'TRIALING';

export interface Plan {
  planId: string;
  name: string;
  slug: string;
  priceMonthly: number;
  priceQuarterly: number;
  priceYearly: number;
  carouselLimit?: number;
  generationLimit?: number;
  exportLimit?: number;
  accessDurationDays?: number | null;
  features: string[];
  highlightColor?: string | null;
  checkoutUrl?: string | null;
}

export interface CurrentSubscription {
  planId: string;
  planName: string;
  planSlug: string;
  period: BillingPeriod;
  status: SubscriptionStatus;
  renewAt: string;
}

export interface PlansResponse {
  currentSubscription: CurrentSubscription | null;
  plans: Plan[];
}

export interface CheckoutPayload {
  planId: string;
  period: BillingPeriod;
  payment: {
    cardNumber: string;
    holderName: string;
    cpfCnpj: string;
    expMonth: string;
    expYear: string;
    cvv: string;
  };
}

export interface CheckoutResponse {
  status: string;
  subscriptionId: string;
  message: string;
}

export interface ChangePlanPayload {
  newPlanId: string;
  period: BillingPeriod;
}

export interface ChangePlanResponse {
  status: string;
  effectiveAt: string;
  message: string;
}

export type InvoiceStatus = 'PAID' | 'PENDING' | 'FAILED';

export interface Invoice {
  id: string;
  planName: string;
  amount: number;
  period: BillingPeriod;
  status: InvoiceStatus;
  paidAt: string | null;
  createdAt: string;
}
